import React, { useState, useEffect } from 'react';
import { Bell, Clock, CheckCircle, X, Calendar, Zap, VolumeX, Volume2 } from 'lucide-react';
import { format, addMinutes, addHours } from 'date-fns';

const AlarmOverlay = ({ task, onSnooze, onPostpone, onComplete, onStopSound }) => {
    const [currentTime, setCurrentTime] = useState(new Date());
    const [showPostponeInput, setShowPostponeInput] = useState(false);
    const [customPostponeTime, setCustomPostponeTime] = useState('');
    const [soundStopped, setSoundStopped] = useState(false);

    useEffect(() => {
        const timer = setInterval(() => setCurrentTime(new Date()), 1000);
        return () => clearInterval(timer);
    }, []);

    // Reset sound stopped state when task changes
    useEffect(() => {
        setSoundStopped(false);
    }, [task?.id]);

    if (!task) return null;

    const handleStopSound = () => {
        if (onStopSound) onStopSound();
        setSoundStopped(true);
    };

    const handleCustomPostpone = () => {
        if (!customPostponeTime) return;
        const [hours, minutes] = customPostponeTime.split(':');
        const newTime = new Date();
        newTime.setHours(parseInt(hours), parseInt(minutes), 0, 0);

        // If time is in past, assume tomorrow
        if (newTime < new Date()) {
            newTime.setDate(newTime.getDate() + 1);
        }

        onPostpone(newTime.getTime());
    };

    return (
        <div className="fixed inset-0 z-[9999] flex flex-col items-center justify-center text-white animate-in fade-in duration-500">
            {/* Animated Gradient Background */}
            <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 animate-gradient-xy"></div>
            <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10"></div>

            {/* Glass Container */}
            <div className="relative z-10 w-full max-w-2xl p-8 md:p-12 flex flex-col items-center">

                {/* Live Clock */}
                <div className="mb-12 text-center">
                    <div className="text-8xl md:text-9xl font-bold font-mono tracking-tighter text-transparent bg-clip-text bg-gradient-to-b from-white to-white/50 drop-shadow-2xl">
                        {format(currentTime, 'h:mm')}
                        <span className="text-4xl md:text-5xl ml-2 text-purple-300">{format(currentTime, 'a')}</span>
                    </div>
                    <div className="text-purple-200 text-xl uppercase tracking-[0.3em] font-semibold mt-2">
                        {format(currentTime, 'EEEE, MMMM d')}
                    </div>
                </div>

                {/* Task Card */}
                <div className="w-full bg-white/10 backdrop-blur-xl border border-white/20 rounded-3xl p-8 md:p-10 shadow-2xl transform transition-all hover:scale-[1.02] duration-500 group">
                    <div className="flex items-center justify-center mb-6">
                        <div className="w-16 h-16 rounded-full bg-purple-500/20 flex items-center justify-center border border-purple-400/30 shadow-[0_0_30px_rgba(168,85,247,0.4)] animate-pulse">
                            <Bell size={32} className="text-purple-300" />
                        </div>
                    </div>

                    <h2 className="text-3xl md:text-4xl font-bold text-center mb-4 text-white leading-tight drop-shadow-md">
                        {task.title}
                    </h2>

                    {task.description && (
                        <p className="text-slate-300 text-lg text-center leading-relaxed max-w-lg mx-auto mb-6">
                            {task.description}
                        </p>
                    )}

                    <div className="flex items-center justify-center gap-3">
                        <div className="flex items-center gap-2 text-sm font-bold text-amber-300 bg-amber-900/30 py-1.5 px-4 rounded-full border border-amber-500/30">
                            <Zap size={14} />
                            <span>ACTION REQUIRED</span>
                        </div>
                    </div>
                </div>

                {/* Motivational Quote */}
                <p className="mt-8 mb-12 text-slate-400 italic text-lg text-center max-w-md">
                    "The only way to do great work is to love what you do."
                </p>

                {/* Actions */}
                {!showPostponeInput ? (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 w-full max-w-xl">
                        <button
                            onClick={handleStopSound}
                            className={`absolute top-8 right-8 p-3 rounded-full transition-colors ${soundStopped ? 'bg-red-500/20 text-red-400 cursor-not-allowed' : 'bg-white/10 hover:bg-white/20 text-white/50 hover:text-white'}`}
                            title="Stop Sound Only"
                            disabled={soundStopped}
                        >
                            {soundStopped ? <VolumeX size={24} /> : <Volume2 size={24} />}
                        </button>

                        <button
                            onClick={() => onSnooze(10)}
                            className="flex flex-col items-center justify-center p-5 bg-slate-800/50 hover:bg-slate-700/50 backdrop-blur-md rounded-2xl border border-white/10 transition-all hover:border-blue-400/50 group"
                        >
                            <Clock className="mb-2 text-blue-400 group-hover:scale-110 transition-transform duration-300" size={28} />
                            <span className="font-semibold text-blue-100">Snooze 10m</span>
                        </button>

                        <button
                            onClick={() => setShowPostponeInput(true)}
                            className="flex flex-col items-center justify-center p-5 bg-slate-800/50 hover:bg-slate-700/50 backdrop-blur-md rounded-2xl border border-white/10 transition-all hover:border-yellow-400/50 group"
                        >
                            <Calendar className="mb-2 text-yellow-400 group-hover:scale-110 transition-transform duration-300" size={28} />
                            <span className="font-semibold text-yellow-100">Postpone</span>
                        </button>

                        <button
                            onClick={() => onComplete(task.id)}
                            className="flex flex-col items-center justify-center p-5 bg-gradient-to-br from-green-600 to-emerald-700 hover:from-green-500 hover:to-emerald-600 rounded-2xl shadow-lg shadow-green-900/40 transition-all transform hover:-translate-y-1 hover:shadow-green-500/20 group"
                        >
                            <CheckCircle className="mb-2 text-white group-hover:scale-110 transition-transform duration-300" size={28} />
                            <span className="font-bold text-white">Complete</span>
                        </button>
                    </div>
                ) : (
                    <div className="w-full max-w-md bg-slate-800/80 backdrop-blur-xl p-8 rounded-3xl border border-white/10 animate-in zoom-in-95 duration-300 shadow-2xl">
                        <h3 className="text-xl font-bold mb-6 text-white text-center">Pick a new time</h3>
                        <input
                            type="time"
                            value={customPostponeTime}
                            onChange={(e) => setCustomPostponeTime(e.target.value)}
                            className="w-full bg-slate-900/50 border border-slate-600 rounded-xl px-4 py-4 text-3xl text-center mb-8 focus:outline-none focus:border-purple-500 text-white font-mono"
                        />
                        <div className="grid grid-cols-2 gap-4">
                            <button
                                onClick={() => setShowPostponeInput(false)}
                                className="py-4 rounded-xl bg-slate-700/50 hover:bg-slate-600/50 text-slate-300 font-medium transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleCustomPostpone}
                                className="py-4 rounded-xl bg-purple-600 hover:bg-purple-500 text-white font-bold shadow-lg shadow-purple-900/30 transition-colors"
                            >
                                Set Reminder
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AlarmOverlay;
